import { DialogCloseEvent } from '@theme/dialog';

const STOREFRONT_API_VERSION = '2025-10';

/** @type {WeakSet<Element>} */
const geolocationModalStarted = new WeakSet();

/** @type {Record<string, Record<string, string>>} */
const GEO_LOCALES = {
  en: {
    titleLocation: 'Your location is set to',
    titleLanguage: 'Your language is set to',
    shopIn: 'Shop in',
    shippingOptions: 'Get shipping options for',
    languageSetTo: 'Language set to',
    continue: 'Continue',
    changeCountryLanguage: 'Change country / language',
    changeLanguage: 'Select language',
    country: 'Country',
    language: 'Language',
    submit: 'Submit',
    back: 'Back',
    close: 'Close',
    infoCountryNotAvailableForShipping:
      "If your country isn't listed, it indicates that shipping to that location is currently unavailable.",
  },
  fr: {
    titleLocation: 'Votre emplacement est défini sur',
    titleLanguage: 'Votre langue est définie sur',
    shopIn: 'Acheter en',
    shippingOptions: 'Obtenir les options de livraison pour',
    languageSetTo: 'Langue définie sur',
    continue: 'Continuer',
    changeCountryLanguage: 'Changer de pays / langue',
    changeLanguage: 'Sélectionner la langue',
    country: 'Pays',
    language: 'Langue',
    submit: 'Soumettre',
    back: 'Retour',
    close: 'Fermer',
    infoCountryNotAvailableForShipping:
      "Si votre pays n'est pas répertorié, cela signifie que la livraison à cet endroit n'est actuellement pas disponible.",
  },
  nl: {
    titleLocation: 'Je locatie is ingesteld op',
    titleLanguage: 'Je taal is ingesteld op',
    shopIn: 'Winkel in',
    shippingOptions: 'Verzendopties voor',
    languageSetTo: 'Taal ingesteld op',
    continue: 'Doorgaan',
    changeCountryLanguage: 'Wijzig land / taal',
    changeLanguage: 'Taal selecteren',
    country: 'Land',
    language: 'Taal',
    submit: 'Versturen',
    back: 'Terug',
    close: 'Sluiten',
    infoCountryNotAvailableForShipping:
      'Als uw land niet in de lijst staat, betekent dit dat verzending naar die locatie momenteel niet beschikbaar is.',
  },
  de: {
    titleLocation: 'Ihre Standort ist auf',
    titleLanguage: 'Ihre Sprache ist auf',
    shopIn: 'Shop in',
    shippingOptions: 'Erhalte Versandoptionen für',
    languageSetTo: 'Sprache auf',
    continue: 'Weiter',
    changeCountryLanguage: 'Land / Sprache ändern',
    changeLanguage: 'Sprache wählen',
    country: 'Land',
    language: 'Sprache',
    submit: 'Einreichen',
    back: 'Zurück',
    close: 'Schließen',
    infoCountryNotAvailableForShipping:
      'Wenn Ihr Land nicht aufgeführt ist, bedeutet dies, dass der Versand an diesen Ort derzeit nicht möglich ist.',
  },
  es: {
    titleLocation: 'Tu ubicación está configurada en',
    titleLanguage: 'Tu idioma está configurado en',
    shopIn: 'Comprar en',
    shippingOptions: 'Obtener opciones de envío para',
    languageSetTo: 'Idioma configurado en',
    continue: 'Continuar',
    changeCountryLanguage: 'Cambiar país / idioma',
    changeLanguage: 'Cambiar idioma',
    country: 'País',
    language: 'Idioma',
    submit: 'Enviar',
    back: 'Volver',
    close: 'Cerrar',
    infoCountryNotAvailableForShipping:
      'Si tu país no está en la lista, esto indica que el envío a esa ubicación no está disponible actualmente.',
  },
  it: {
    titleLocation: 'La tua posizione è impostata su',
    titleLanguage: 'La tua lingua è impostata su',
    shopIn: 'Compra in',
    shippingOptions: 'Ottieni opzioni di spedizione per',
    languageSetTo: 'Lingua impostata su',
    continue: 'Continua',
    changeCountryLanguage: 'Cambia paese / lingua',
    changeLanguage: 'Lingua selezionare',
    country: 'Paese',
    language: 'Lingua',
    submit: 'Invia',
    back: 'Indietro',
    close: 'Chiudi',
    infoCountryNotAvailableForShipping:
      'Se il tuo paese non è elencato, significa che la spedizione verso quella località non è attualmente disponibile.',
  },
  pt: {
    titleLocation: 'Sua localização está definida em',
    titleLanguage: 'Seu idioma está definido em',
    shopIn: 'Comprar em',
    shippingOptions: 'Obtenha opções de envio para',
    languageSetTo: 'Idioma definido em',
    continue: 'Continuar',
    changeCountryLanguage: 'Alterar país / idioma',
    changeLanguage: 'Selecionar idioma',
    country: 'País',
    language: 'Idioma',
    submit: 'Enviar',
    back: 'Voltar',
    close: 'Fechar',
    infoCountryNotAvailableForShipping:
      'Se o seu país não estiver listado, isso indica que o envio para esse local não está disponível no momento.',
  },
};

/**
 * @param {string | undefined} storefrontApiAccessToken
 * @param {string} query
 * @param {Record<string, unknown>} [variables]
 */
async function fetchStorefrontApi(storefrontApiAccessToken, query, variables = {}) {
  if (!storefrontApiAccessToken) {
    console.error('Geolocation modal: Storefront API access token is not set');
    return null;
  }

  const shop = /** @type {{ shop?: string }} */ (window.Shopify)?.shop;
  if (!shop) return null;

  const endpoint = `https://${shop}/api/${STOREFRONT_API_VERSION}/graphql.json`;
  let response;
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontApiAccessToken,
      },
      body: JSON.stringify({ query, variables }),
    });
  } catch (networkError) {
    console.warn('Geolocation modal: Storefront API request failed', networkError);
    return null;
  }

  if (!response.ok) {
    console.warn(
      'Geolocation modal: Storefront API HTTP error',
      response.status,
      response.statusText
    );
    return null;
  }

  let result;
  try {
    result = await response.json();
  } catch (parseError) {
    console.warn('Geolocation modal: Storefront API response was not valid JSON', parseError);
    return null;
  }

  const { data, errors } = result;

  if (errors?.length) {
    console.warn('Geolocation modal: GraphQL errors', errors);
    return null;
  }

  return data ?? null;
}

/**
 * Converts a locale handle (BCP 47-style, e.g. en-GB, pt-BR) to a Storefront API
 * `LanguageCode` enum literal (e.g. EN_GB, PT_BR). Hyphens become underscores; the
 * API does not accept IETF-style EN-GB in `@inContext`.
 * @param {string | null | undefined} localeHandle
 * @returns {string}
 */
function localeHandleToStorefrontLanguageCode(localeHandle) {
  if (localeHandle == null || typeof localeHandle !== 'string') {
    return 'EN';
  }
  const trimmed = localeHandle.trim();
  if (!trimmed) {
    return 'EN';
  }
  return trimmed.replace(/-/g, '_').toUpperCase();
}

/**
 * Inverse of {@link localeHandleToStorefrontLanguageCode}: maps Storefront
 * `Language.isoCode` enum values (e.g. EN, EN_GB) to BCP 47 tags Shopify expects
 * for `/localization` and `Shopify.locale` (e.g. en, en-GB).
 * @param {string | null | undefined} storefrontCode
 * @returns {string}
 */
function storefrontLanguageCodeToLocaleHandle(storefrontCode) {
  if (storefrontCode == null || typeof storefrontCode !== 'string') {
    return '';
  }
  const trimmed = storefrontCode.trim();
  if (!trimmed) {
    return '';
  }
  const parts = trimmed.split('_').filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  const firstPart = parts[0];
  if (firstPart === undefined) {
    return '';
  }
  const lang = firstPart.toLowerCase();
  if (parts.length === 1) {
    return lang;
  }
  const subtags = parts.slice(1).map((segment) => {
    if (segment.length === 2 && /^[A-Za-z]{2}$/.test(segment)) {
      return segment.toUpperCase();
    }
    if (segment.length === 4 && /^[A-Za-z]{4}$/.test(segment)) {
      return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    }
    return segment;
  });
  return [lang, ...subtags].join('-');
}

/**
 * Canonical Shopify locale string for forms and comparisons (handles suggestion
 * handles, Storefront enums, and minor casing variants).
 * @param {string | null | undefined} handle
 * @returns {string}
 */
function normalizeShopifyLanguageCode(handle) {
  if (handle == null || typeof handle !== 'string' || !handle.trim()) {
    return '';
  }
  return storefrontLanguageCodeToLocaleHandle(localeHandleToStorefrontLanguageCode(handle));
}

/**
 * @param {string | null | undefined} a
 * @param {string | null | undefined} b
 * @returns {boolean}
 */
function shopifyLanguageCodesEqual(a, b) {
  return normalizeShopifyLanguageCode(a) === normalizeShopifyLanguageCode(b);
}

/**
 * Primary language subtag only (e.g. en-GB → EN) for Storefront fallback when the
 * region-specific enum is rejected or unavailable.
 * @param {string | null | undefined} localeHandle
 * @returns {string}
 */
function primaryLocaleToStorefrontLanguageCode(localeHandle) {
  if (localeHandle == null || typeof localeHandle !== 'string') {
    return 'EN';
  }
  const trimmed = localeHandle.trim();
  if (!trimmed) {
    return 'EN';
  }
  const first = trimmed.split(/[-_]/)[0];
  return first ? first.toUpperCase() : 'EN';
}

/**
 * Unique ordered candidates for `@inContext(language: …)` (full code, then primary if different).
 * @param {string | null | undefined} localeHandle
 * @returns {string[]}
 */
function storefrontLanguageCodeCandidates(localeHandle) {
  const full = localeHandleToStorefrontLanguageCode(localeHandle);
  const primary = primaryLocaleToStorefrontLanguageCode(localeHandle);
  if (primary === full) {
    return [full];
  }
  return [full, primary];
}

/**
 * @param {string} storefrontIsoCode Language.isoCode from the Storefront API (already enum-shaped).
 * @param {string | null | undefined} localeHandle Theme/suggestion handle (may use hyphens).
 * @returns {boolean}
 */
function languageIsoCodesEqual(storefrontIsoCode, localeHandle) {
  return localeHandleToStorefrontLanguageCode(localeHandle) === storefrontIsoCode;
}

/**
 * @param {Record<string, unknown>} suggestions
 * @param {{ availableCountries: unknown[] }} localization
 */
/**
 * Representative data for theme editor preview (no Storefront API or browsing suggestions).
 * Matches the shape returned by {@link GeolocationModal.fetchGeolocationSuggestions} and
 * {@link GeolocationModal.fetchLocalization}.
 */
function getDesignModeMockLocalizationData() {
  return {
    suggestions: {
      recommendedLanguage: 'English',
      recommendedLanguageCode: 'en',
      recommendedCountry: 'United States',
      recommendedCountryCode: 'US',
      recommendedCurrency: 'USD',
    },
    localization: {
      availableCountries: [
        {
          isoCode: 'US',
          name: 'United States',
          currency: { isoCode: 'USD' },
          availableLanguages: [
            { isoCode: 'EN', endonymName: 'English' },
            { isoCode: 'ES', endonymName: 'Español' },
          ],
        },
        {
          isoCode: 'CA',
          name: 'Canada',
          currency: { isoCode: 'CAD' },
          availableLanguages: [
            { isoCode: 'EN', endonymName: 'English' },
            { isoCode: 'FR', endonymName: 'Français' },
          ],
        },
      ],
    },
  };
}

/**
 * Builds modal markup with the DOM API so runtime strings (API / suggestions) are never
 * concatenated into HTML.
 * @param {{
 *   recommendedCountry?: string | null;
 *   recommendedCountryCode?: string | null;
 *   recommendedCurrency?: string | null;
 *   recommendedLanguage?: string | null;
 *   recommendedLanguageCode?: string | null;
 * }} suggestions
 * @param {{ availableCountries: unknown[] }} localization
 * @returns {DocumentFragment}
 */
function buildGeolocationModalFragment(suggestions, localization) {
  const {
    recommendedCountry,
    recommendedCountryCode,
    recommendedCurrency,
    recommendedLanguage,
    recommendedLanguageCode,
  } = suggestions;

  const normalizedRecommendationLang = recommendedLanguageCode
    ? normalizeShopifyLanguageCode(recommendedLanguageCode)
    : '';
  const translationLocaleKey = normalizedRecommendationLang.split('-')[0] || 'en';
  const localePack =
    translationLocaleKey in GEO_LOCALES
      ? GEO_LOCALES[/** @type {keyof typeof GEO_LOCALES} */ (translationLocaleKey)]
      : GEO_LOCALES.en;
  /** @type {(typeof GEO_LOCALES)['en']} */
  const translations = /** @type {(typeof GEO_LOCALES)['en']} */ (localePack ?? GEO_LOCALES.en);

  const recommendedLanguageStorefrontCode = recommendedLanguageCode
    ? localeHandleToStorefrontLanguageCode(recommendedLanguageCode)
    : 'EN';

  const availableCountries = localization.availableCountries;
  const showRecommendedCountryLanguage = availableCountries.some(
    (country) =>
      /** @type {{ isoCode: string; availableLanguages: { isoCode: string }[] }} */ (country).isoCode ===
        recommendedCountryCode &&
      /** @type {{ isoCode: string; availableLanguages: { isoCode: string }[] }} */ (
        country
      ).availableLanguages.some((language) => language.isoCode === recommendedLanguageStorefrontCode)
  );

  const localizationCountriesCount = availableCountries.length;
  const localizationCurrenciesAreDifferent = availableCountries.some(
    (country) =>
      /** @type {{ currency?: { isoCode: string } }} */ (country).currency?.isoCode !== recommendedCurrency
  );

  /** @type {{ isoCode: string; label: string; selected: boolean }[]} */
  const localizationCountryOptionsData = availableCountries.map((country) => {
    const { isoCode, name, currency } =
      /** @type {{
        isoCode: string;
        name: string;
        currency?: { isoCode: string };
        availableLanguages: unknown[];
      }} */ (country);
    let currencyIsoCode = null;
    if (currency && localizationCurrenciesAreDifferent) {
      currencyIsoCode = currency.isoCode;
    }
    const label = currencyIsoCode ? `${name} (${currencyIsoCode})` : name;
    return {
      isoCode,
      label,
      selected: isoCode === recommendedCountryCode,
    };
  });

  const showRecommendedCountry = recommendedCountry && localizationCountriesCount > 1;
  let recommendedCountryFlagUrl = '';
  if (showRecommendedCountry && recommendedCountryCode) {
    recommendedCountryFlagUrl = `https://cdn.shopify.com/static/images/flags/${String(recommendedCountryCode).toLowerCase()}.svg`;
  }

  const pathname = window.location.pathname;

  const fragment = document.createDocumentFragment();

  if (showRecommendedCountryLanguage) {
    const recommendedRoot = document.createElement('div');
    recommendedRoot.className = 'geolocation__content js-geolocation-recommended';

    if (showRecommendedCountry && recommendedCountryFlagUrl) {
      const flagWrap = document.createElement('div');
      flagWrap.className = 'geolocation__flag';
      const flagImg = document.createElement('img');
      flagImg.src = recommendedCountryFlagUrl;
      flagImg.alt = String(recommendedCountry ?? '');
      flagImg.width = 110;
      flagImg.height = 70;
      flagImg.loading = 'lazy';
      flagWrap.appendChild(flagImg);
      recommendedRoot.appendChild(flagWrap);
    }

    const titleP = document.createElement('p');
    titleP.className = 'geolocation__title';
    titleP.textContent = showRecommendedCountry
      ? `${translations.titleLocation} ${recommendedCountry}`
      : `${translations.titleLanguage} ${recommendedLanguage}`;
    recommendedRoot.appendChild(titleP);

    if (showRecommendedCountry) {
      const list = document.createElement('ul');
      list.className = 'geolocation__body';
      if (recommendedCurrency && localizationCurrenciesAreDifferent) {
        const li = document.createElement('li');
        li.textContent = `${translations.shopIn} ${recommendedCurrency}`;
        list.appendChild(li);
      }
      if (recommendedCountry) {
        const li = document.createElement('li');
        li.textContent = `${translations.shippingOptions} ${recommendedCountry}`;
        list.appendChild(li);
      }
      if (recommendedLanguage) {
        const li = document.createElement('li');
        li.textContent = `${translations.languageSetTo} ${recommendedLanguage}`;
        list.appendChild(li);
      }
      recommendedRoot.appendChild(list);
    }

    const ctas = document.createElement('div');
    ctas.className = 'geolocation__ctas';

    const formContinue = document.createElement('form');
    formContinue.action = '/localization';
    formContinue.method = 'post';

    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'PUT';
    formContinue.appendChild(methodInput);

    const returnInput = document.createElement('input');
    returnInput.type = 'hidden';
    returnInput.name = 'return_to';
    returnInput.value = pathname;
    formContinue.appendChild(returnInput);

    const countryHidden = document.createElement('input');
    countryHidden.type = 'hidden';
    countryHidden.name = 'country_code';
    countryHidden.value = recommendedCountryCode != null ? String(recommendedCountryCode) : '';
    formContinue.appendChild(countryHidden);

    const languageHidden = document.createElement('input');
    languageHidden.type = 'hidden';
    languageHidden.name = 'language_code';
    languageHidden.value = normalizedRecommendationLang;
    formContinue.appendChild(languageHidden);

    const submitContinue = document.createElement('button');
    submitContinue.type = 'submit';
    submitContinue.className = 'button js-geolocation-submit geolocation__submit-button';
    submitContinue.textContent = translations.continue ?? '';
    formContinue.appendChild(submitContinue);

    ctas.appendChild(formContinue);

    const changeBtn = document.createElement('button');
    changeBtn.type = 'button';
    changeBtn.className = 'geolocation__cta js-geolocation-change';
    changeBtn.textContent = showRecommendedCountry
      ? (translations.changeCountryLanguage ?? '')
      : (translations.changeLanguage ?? '');
    ctas.appendChild(changeBtn);

    recommendedRoot.appendChild(ctas);
    fragment.appendChild(recommendedRoot);
  }

  const selectRoot = document.createElement('div');
  selectRoot.className = 'geolocation__content js-geolocation-select';
  if (showRecommendedCountryLanguage) {
    selectRoot.style.display = 'none';
  }

  const selectTitle = document.createElement('div');
  selectTitle.className = 'geolocation__title';
  selectTitle.textContent = translations.changeCountryLanguage ?? '';
  selectRoot.appendChild(selectTitle);

  if (!showRecommendedCountry) {
    const info = document.createElement('div');
    info.className = 'geolocation__info';
    info.textContent = translations.infoCountryNotAvailableForShipping ?? '';
    selectRoot.appendChild(info);
  }

  const formWrapper = document.createElement('div');
  formWrapper.className = 'geolocation__form-wrapper';

  const formSelect = document.createElement('form');
  formSelect.action = '/localization';
  formSelect.method = 'post';
  formSelect.className = 'geolocation__form';

  const methodInput2 = document.createElement('input');
  methodInput2.type = 'hidden';
  methodInput2.name = '_method';
  methodInput2.value = 'PUT';
  formSelect.appendChild(methodInput2);

  const returnInput2 = document.createElement('input');
  returnInput2.type = 'hidden';
  returnInput2.name = 'return_to';
  returnInput2.value = pathname;
  formSelect.appendChild(returnInput2);

  const countryRow = document.createElement('div');
  countryRow.className = 'geolocation__select';
  const countryLabel = document.createElement('label');
  countryLabel.htmlFor = 'GeolocationCountrySelect';
  countryLabel.className = 'visually-hidden';
  countryLabel.textContent = translations.country ?? '';
  const countrySelect = document.createElement('select');
  countrySelect.id = 'GeolocationCountrySelect';
  countrySelect.name = 'country_code';
  countrySelect.className = 'geolocation__select-input';
  for (const opt of localizationCountryOptionsData) {
    const option = document.createElement('option');
    option.value = opt.isoCode;
    option.textContent = opt.label;
    if (opt.selected) option.selected = true;
    countrySelect.appendChild(option);
  }
  countryRow.appendChild(countryLabel);
  countryRow.appendChild(countrySelect);
  formSelect.appendChild(countryRow);

  const languageRow = document.createElement('div');
  languageRow.className = 'geolocation__select';
  const languageLabel = document.createElement('label');
  languageLabel.htmlFor = 'GeolocationLanguageSelect';
  languageLabel.className = 'visually-hidden';
  languageLabel.textContent = translations.language ?? '';
  const languageSelect = document.createElement('select');
  languageSelect.id = 'GeolocationLanguageSelect';
  languageSelect.name = 'language_code';
  languageSelect.className = 'geolocation__select-input';
  languageRow.appendChild(languageLabel);
  languageRow.appendChild(languageSelect);
  formSelect.appendChild(languageRow);

  const submitSelect = document.createElement('button');
  submitSelect.type = 'submit';
  submitSelect.className = 'button js-geolocation-submit geolocation__submit-button';
  submitSelect.textContent = translations.submit ?? '';
  formSelect.appendChild(submitSelect);

  formWrapper.appendChild(formSelect);

  if (showRecommendedCountryLanguage) {
    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'geolocation__cta js-geolocation-back';
    backBtn.textContent = translations.back ?? '';
    formWrapper.appendChild(backBtn);
  }

  selectRoot.appendChild(formWrapper);
  fragment.appendChild(selectRoot);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'button-unstyled geolocation__close js-geolocation-close';
  closeBtn.setAttribute('aria-label', translations.close ?? '');

  const svgNs = 'http://www.w3.org/2000/svg';
  const closeSvg = document.createElementNS(svgNs, 'svg');
  closeSvg.setAttribute('viewBox', '0 0 17.1 17.1');
  closeSvg.setAttribute('width', '17');
  closeSvg.setAttribute('height', '17');
  closeSvg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svgNs, 'path');
  path.setAttribute(
    'd',
    'M17.1 1.1 16 0 8.5 7.5 1.1 0 0 1.1l7.5 7.4L0 16l1.1 1.1 7.4-7.5 7.5 7.5 1.1-1.1-7.5-7.5 7.5-7.4z'
  );
  closeSvg.appendChild(path);
  closeBtn.appendChild(closeSvg);

  const closeSpan = document.createElement('span');
  closeSpan.className = 'visually-hidden';
  closeSpan.textContent = translations.close ?? '';
  closeBtn.appendChild(closeSpan);

  fragment.appendChild(closeBtn);

  return fragment;
}

class GeolocationModal {
  constructor() {
    this.modalNamespace = 'geolocation-modal';
    /** @type {HTMLElement | null} */
    this.modalEl = document.getElementById(this.modalNamespace);

    if (!this.modalEl) return;

    /** Theme editor: render mock UI only; no APIs, cookies, or auto-open. */
    if (window.Shopify?.designMode) {
      this.#loadDesignModePreview();
      return;
    }

    this.cookieName = 'geolocation_modal_accepted';
    this.cookieValue = '1';
    this.defaultLanguage = 'EN';

    const cookieRow = document.cookie.split('; ').find((row) => row.startsWith(`${this.cookieName}=`));
    const currentCookieValue = cookieRow ? cookieRow.split('=')[1]?.toString() : null;

    const key = /** @type {HTMLElement} */ (this.modalEl).dataset.key;
    this.storefrontApiAccessToken = key ?? '';

    if (!currentCookieValue || currentCookieValue !== this.cookieValue) {
      this.#bindDialogClose();
      this.getModal();
    }
  }

  setGeolocationCookie() {
    document.cookie = `${this.cookieName}=${this.cookieValue}; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT; SameSite=Lax`;
  }

  #dialogCloseListener = () => {
    this.setGeolocationCookie();
  };

  #bindDialogClose() {
    const el = this.modalEl;
    if (!el || el.dataset.geolocationCloseBound === 'true') return;
    el.dataset.geolocationCloseBound = 'true';
    el.addEventListener(DialogCloseEvent.eventName, this.#dialogCloseListener);
  }

  #loadDesignModePreview() {
    if (!this.modalEl || geolocationModalStarted.has(this.modalEl)) return;
    geolocationModalStarted.add(this.modalEl);

    const { suggestions, localization } = getDesignModeMockLocalizationData();
    this.suggestions = suggestions;
    this.localization = localization;

    this.modalEl.setAttribute('data-geolocation-design-mode', '');

    const modalFragment = buildGeolocationModalFragment(this.suggestions, this.localization);
    const modalContainerEl = document.getElementById('geolocation-container');
    if (modalContainerEl) {
      modalContainerEl.replaceChildren(modalFragment);
    }

    this.handleFormEvents();

    this.modalEl.querySelectorAll('form').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
      });
    });

    this.modalEl?.querySelectorAll('.js-geolocation-close').forEach((el) => {
      el.addEventListener('click', () => {
        /** @type {{ closeDialog?: () => void }} */ (this.modalEl)?.closeDialog?.();
      });
    });
  }

  async fetchGeolocationSuggestions() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const suggestionsResponse = await fetch(
        `${root}browsing_context_suggestions.json?country[enabled]=true&currency[enabled]=true&language[enabled]=true`
      ).then((r) => r.json());

      let recommendedLanguage = null;
      let recommendedLanguageCode = null;
      let recommendedCountry = null;
      let recommendedCountryCode = null;
      let recommendedCurrency = null;

      if (suggestionsResponse?.suggestions?.length > 0) {
        const suggestions = suggestionsResponse.suggestions[0];
        const {
          country: countryParts,
          language: languageParts,
          currency: currencyParts,
        } = suggestions.parts ?? {};

        if (languageParts) {
          recommendedLanguage = languageParts.name;
          recommendedLanguageCode = languageParts.handle;
        }

        if (countryParts) {
          recommendedCountry = countryParts.name;
          recommendedCountryCode = countryParts.handle;
        }

        if (currencyParts) {
          recommendedCurrency = currencyParts.handle;
        }
      }

      return {
        recommendedLanguage,
        recommendedLanguageCode,
        recommendedCountry,
        recommendedCountryCode,
        recommendedCurrency,
      };
    } catch (error) {
      console.error('Error fetching geolocation suggestions:', error);
      return null;
    }
  }

  async fetchLocalization() {
    const defaultLanguageCode = this.defaultLanguage;
    const languageCode = this.suggestions?.recommendedLanguageCode || defaultLanguageCode;

    /** @param {string} code */
    const buildLocalizationQuery = (code) => `
      query AllLocalizations @inContext(language: ${code}) {
        localization {
          availableCountries {
            isoCode
            name
            currency {
              isoCode
            }
            availableLanguages {
              isoCode
              endonymName
            }
          }
        }
      }
    `;

    const candidates = storefrontLanguageCodeCandidates(languageCode);

    for (const code of candidates) {
      const response = await fetchStorefrontApi(
        this.storefrontApiAccessToken,
        buildLocalizationQuery(code)
      );
      if (response) {
        return /** @type {{ localization: { availableCountries: unknown[] } }} */ (response).localization;
      }
    }

    return null;
  }

  async getModal() {
    try {
      await this.#loadModal();
    } catch (error) {
      console.error('Geolocation modal: failed to initialize', error);
      this.setGeolocationCookie();
    }
  }

  async #loadModal() {
    this.suggestions = await this.fetchGeolocationSuggestions();
    if (!this.suggestions) {
      this.setGeolocationCookie();
      return;
    }

    let isCurrentMarketDifferentFromRecommendedMarket = true;
    if (this.suggestions.recommendedLanguageCode && this.suggestions.recommendedCountryCode) {
      isCurrentMarketDifferentFromRecommendedMarket =
        !shopifyLanguageCodesEqual(window.Shopify.locale, this.suggestions.recommendedLanguageCode) ||
        window.Shopify.country !== this.suggestions.recommendedCountryCode;
    }

    if (isCurrentMarketDifferentFromRecommendedMarket) {
      this.localization = await this.fetchLocalization();
      if (!this.localization) {
        this.setGeolocationCookie();
        return;
      }
    }

    if (
      !this.suggestions.recommendedLanguageCode &&
      this.suggestions.recommendedCountryCode &&
      this.localization?.availableCountries
    ) {
      const selectedCountry = this.localization.availableCountries.find(
        (country) =>
          /** @type {{ isoCode: string }} */ (country).isoCode === this.suggestions.recommendedCountryCode
      );

      const isOnlyOneLanguage =
        selectedCountry &&
        /** @type {{ availableLanguages: unknown[] }} */ (selectedCountry).availableLanguages.length === 1;

      if (isOnlyOneLanguage && selectedCountry) {
        const selectedLanguage =
          /** @type {{ availableLanguages: { isoCode: string; endonymName: string }[] }} */ (selectedCountry)
            .availableLanguages[0];
        this.suggestions.recommendedLanguageCode = storefrontLanguageCodeToLocaleHandle(
          selectedLanguage.isoCode
        );
        this.suggestions.recommendedLanguage = selectedLanguage.endonymName;

        this.localization = await this.fetchLocalization();
        if (!this.localization) {
          this.setGeolocationCookie();
          return;
        }

        const selectedCountryLocalization = this.localization.availableCountries.find(
          (country) =>
            /** @type {{ isoCode: string }} */ (country).isoCode === this.suggestions.recommendedCountryCode
        );
        if (selectedCountryLocalization) {
          this.suggestions.recommendedCountry =
            /** @type {{ name: string }} */ (selectedCountryLocalization).name;
          this.suggestions.recommendedCountryCode =
            /** @type {{ isoCode: string }} */ (selectedCountryLocalization).isoCode;
        }

        isCurrentMarketDifferentFromRecommendedMarket =
          !shopifyLanguageCodesEqual(window.Shopify.locale, this.suggestions.recommendedLanguageCode) ||
          window.Shopify.country !== this.suggestions.recommendedCountryCode;
      }
    }

    if (!isCurrentMarketDifferentFromRecommendedMarket) {
      this.setGeolocationCookie();
    }

    if (this.localization && isCurrentMarketDifferentFromRecommendedMarket) {
      if (!this.modalEl || geolocationModalStarted.has(this.modalEl)) return;
      geolocationModalStarted.add(this.modalEl);

      const modalFragment = buildGeolocationModalFragment(this.suggestions, this.localization);
      const modalContainerEl = document.getElementById('geolocation-container');
      if (modalContainerEl) {
        modalContainerEl.replaceChildren(modalFragment);
      }

      this.handleFormEvents();

      /** @type {{ showDialog?: () => void }} */
      const dialogHost = this.modalEl;
      dialogHost?.showDialog?.();

      this.modalEl?.querySelectorAll('.js-geolocation-close').forEach((el) => {
        el.addEventListener('click', () => {
          /** @type {{ closeDialog?: () => void }} */ (this.modalEl)?.closeDialog?.();
          this.setGeolocationCookie();
        });
      });

      this.modalEl?.querySelectorAll('.js-geolocation-submit').forEach((el) => {
        el.addEventListener('click', () => {
          this.setGeolocationCookie();
        });
      });
    }
  }

  handleFormEvents() {
    if (!this.modalEl) return;

    this.countrySelect = this.modalEl.querySelector('select[name="country_code"]');
    this.languageSelect = this.modalEl.querySelector('select[name="language_code"]');
    this.recommendedModal = this.modalEl.querySelector('.js-geolocation-recommended');
    this.selectMarketModal = this.modalEl.querySelector('.js-geolocation-select');

    this.changeButton = this.modalEl.querySelector('.js-geolocation-change');
    this.backButton = this.modalEl.querySelector('.js-geolocation-back');

    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', this.handleCountrySelectChange.bind(this));
    }

    if (this.changeButton) {
      this.changeButton.addEventListener('click', this.handleViewSelectMarket.bind(this));
    }

    if (this.backButton) {
      this.backButton.addEventListener('click', this.handleViewRecommended.bind(this));
    }

    this.renderLanguageOptions(
      this.suggestions?.recommendedCountryCode,
      this.suggestions?.recommendedLanguageCode
    );
  }

  handleCountrySelectChange() {
    if (!this.countrySelect) return;
    const selectedCountry = /** @type {HTMLSelectElement} */ (this.countrySelect).value;
    this.renderLanguageOptions(selectedCountry, this.suggestions?.recommendedLanguageCode);
  }

  /**
   * @param {string | null | undefined} countryCode
   * @param {string | null | undefined} preselectedLanguageCodeSuggestion
   */
  renderLanguageOptions(countryCode, preselectedLanguageCodeSuggestion) {
    const languageSelect = this.languageSelect;
    if (!this.localization?.availableCountries?.length || !languageSelect) {
      return;
    }

    const preselectedLanguageCode = preselectedLanguageCodeSuggestion || this.defaultLanguage;
    let selectedCountry = this.localization.availableCountries.find(
      (country) => /** @type {{ isoCode: string }} */ (country).isoCode === countryCode
    );

    if (!selectedCountry) {
      selectedCountry = this.localization.availableCountries[0];
    }

    let preselectedLanguage = null;
    if (selectedCountry) {
      preselectedLanguage = /** @type {{ availableLanguages: { isoCode: string }[] }} */ (
        selectedCountry
      ).availableLanguages.find((language) =>
        languageIsoCodesEqual(language.isoCode, preselectedLanguageCode)
      );
    }

    let selectedLanguageCode = preselectedLanguage
      ? storefrontLanguageCodeToLocaleHandle(preselectedLanguage.isoCode)
      : null;

    if (!selectedLanguageCode && selectedCountry) {
      const currentLanguageCode = window.Shopify.locale;
      const currentLanguage = /** @type {{ availableLanguages: { isoCode: string }[] }} */ (
        selectedCountry
      ).availableLanguages.find((language) =>
        languageIsoCodesEqual(language.isoCode, currentLanguageCode)
      );
      selectedLanguageCode = currentLanguage
        ? storefrontLanguageCodeToLocaleHandle(currentLanguage.isoCode)
        : null;
    }

    if (!selectedLanguageCode && selectedCountry) {
      const langsFallback =
        /** @type {{ availableLanguages: { isoCode: string }[] }} */ (selectedCountry).availableLanguages;
      const fallbackLang = langsFallback[0];
      if (fallbackLang) {
        selectedLanguageCode = storefrontLanguageCodeToLocaleHandle(fallbackLang.isoCode);
      }
    }

    if (!selectedCountry) {
      languageSelect.replaceChildren();
      return;
    }

    const langs =
      /** @type {{ availableLanguages: { isoCode: string; endonymName: string }[] }} */ (selectedCountry)
        .availableLanguages;

    languageSelect.replaceChildren();
    for (const language of langs) {
      const formValue = storefrontLanguageCodeToLocaleHandle(language.isoCode);
      const isSelected =
        Boolean(selectedLanguageCode) && languageIsoCodesEqual(language.isoCode, selectedLanguageCode);
      const option = document.createElement('option');
      option.value = formValue;
      option.textContent = language.endonymName;
      if (isSelected) option.selected = true;
      languageSelect.appendChild(option);
    }
  }

  handleViewSelectMarket() {
    if (this.recommendedModal instanceof HTMLElement) this.recommendedModal.style.display = 'none';
    if (this.selectMarketModal instanceof HTMLElement) this.selectMarketModal.style.display = 'block';
  }

  handleViewRecommended() {
    if (this.recommendedModal instanceof HTMLElement) this.recommendedModal.style.display = 'block';
    if (this.selectMarketModal instanceof HTMLElement) this.selectMarketModal.style.display = 'none';
  }
}

function initGeolocationModal() {
  const el = document.getElementById('geolocation-modal');
  if (!el) return;
  new GeolocationModal();
}

initGeolocationModal();

document.addEventListener('shopify:section:load', () => {
  initGeolocationModal();
});
